import React, { useEffect, useState } from "react";
import Slider from "react-slick";
import { Box, Typography, Skeleton } from "@mui/material";
import { AxiosResponse } from "axios";
import { serviceFetchTodaysDeals } from "src/api/service/services";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "./TodayDeals.css";
import default_image from "../../assets/default_sale_tag.png"; // your default image
import { getRetailerLogo } from "src/utils/retailers_list";

const TodaysDealBanner = () => {
  const [productDealData, setProductDealData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    serviceFetchTodaysDeals()
      .then((response: AxiosResponse) => {
        const validDeals = (response?.data || [])
          .filter(
            (item: any) =>
              item?.acf?.todays_deal_url &&
              item?.acf?.todays_deal_url.trim() !== ""
          )
          .map((item: any) => {
            const image = item?.acf?.todays_deal_image;
            if (!image || image.trim() === "") {
              item.acf.todays_deal_image = default_image;
            }
            return item;
          });

        setProductDealData(validDeals);
      })
      .finally(() => setLoading(false));
  }, []);

  const settings = {
    dots: true,
    autoplay: true,
    autoplaySpeed: 3000,
    pauseOnHover: false,
    speed: 600,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: false,
  };

  // 🟡 If still loading and no data yet — show Skeleton
  if (loading && productDealData.length === 0) {
    return (
      <Box sx={{ width: "100%", borderRadius: "16px", overflow: "hidden" }}>
        <Skeleton
          variant="rounded"
          height={250}
          animation="wave"
          sx={{ borderRadius: "16px" }}
        />
      </Box>
    );
  }

  // 🔴 If loading finished and no valid deals — show nothing
  if (!loading && productDealData.length === 0) {
    return null;
  }

  // 🟢 Show the slider once we have valid data
  return (
    <Box
      sx={{
        width: "100%",
        position: "relative",
        borderRadius: "16px",
        overflow: "hidden",
        boxShadow: "0px 4px 20px rgba(0,0,0,0.15)",
        backgroundColor: "transparent",
      }}
    >
      <Slider {...settings}>
        {productDealData.map((item, index) => {
          const retailer = item?.acf?.todays_deal_retailer || "";
          const retailerLogo = getRetailerLogo(retailer);

          return (
            <Box
              key={index}
              sx={{ position: "relative", cursor: "pointer" }}
              onClick={() => {
                const dealUrl = item?.acf?.todays_deal_url;
                if (dealUrl) window.open(dealUrl, "_blank");
              }}
            >
              {/* Background Image */}
              <Box
                component="img"
                src={item?.acf?.todays_deal_image}
                alt={item?.acf?.todays_deal_alt || `Today's Deal ${index + 1}`}
                sx={{
                  width: "100%",
                  height: { xs: 150, sm: 250, md: 350 },
                  objectFit: "contain",
                  borderRadius: "16px",
                }}
              />

              {/* Gradient overlay */}
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  background:
                    "linear-gradient(to bottom, rgba(0,0,0,0.2), rgba(0,0,0,0.6))",
                  pointerEvents: "none",
                }}
              />

              {/* Text + Retailer Logo Overlay */}
              <Box
                sx={{
                  position: "absolute",
                  bottom: 20,
                  left: 20,
                  color: "#fff",
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                }}
              >
                <Typography
                  variant="h5"
                  fontWeight="bold"
                  sx={{
                    textShadow: "0px 1px 4px rgba(0,0,0,0.6)",
                    fontSize: { xs: "1rem", sm: "1.3rem", md: "1.5rem" },
                  }}
                >
                  {item?.title?.rendered || "Today's Deal"}
                </Typography>

                {retailer && (
                  <Box sx={{ display: "flex", alignItems: "center", mt: 0.5 }}>
                    <Box
                      component="img"
                      src={retailerLogo}
                      alt={retailer}
                      sx={{
                        width: { xs: 50, sm: 70 },
                        height: "auto",
                        objectFit: "contain",
                        backgroundColor: "rgba(255,255,255,0.85)",
                        borderRadius: "6px",
                        p: "3px 6px",
                        boxShadow: "0 2px 5px rgba(0,0,0,0.2)",
                      }}
                    />
                  </Box>
                )}
              </Box>
            </Box>
          );
        })}
      </Slider>
    </Box>
  );
};

export default TodaysDealBanner;
